// @ts-nocheck
// client/src/v2/nota-editor.tsx
// O EDITOR DE NOTA LONGA — tela cheia, Markdown.
//
// Por que Markdown e não JSON de editor: a nota tem que sobreviver ao app.
// Markdown é portável, pesquisável com um `includes` e exportável sem
// conversor. E a nota antiga, que é texto puro, já é Markdown válido — não
// houve nada a migrar.
//
// A barra não é um editor rico: ela ESCREVE a marcação no texto. O que a
// pessoa vê é o que está guardado, e o que ela copia para outro lugar
// continua fazendo sentido.
//
// `#tag` e `[[nota]]` se escrevem no corpo; o servidor deriva as relações na
// gravação. Aqui só existe o autocompletar, para não obrigar ninguém a
// lembrar o nome exato.
//
// Zero cor literal: 4 paletas, 2 delas claras.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../services/apiV2';
import { Icon } from './icons';

const EYEBROW: React.CSSProperties = {
  fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.16em',
  textTransform: 'uppercase', fontWeight: 600, color: 'var(--ink-4)',
};

/** Cada botão da barra: o que enfia no texto e onde deixa o cursor. */
const FERRAMENTAS = [
  { rotulo: 'H', titulo: 'Título',    prefixo: '## ',   linha: true },
  { rotulo: 'B', titulo: 'Negrito',   volta: '**',      envolve: '**' },
  { rotulo: 'I', titulo: 'Itálico',   volta: '_',       envolve: '_' },
  { rotulo: '•', titulo: 'Lista',     prefixo: '- ',    linha: true },
  { rotulo: '☑', titulo: 'Checklist', prefixo: '- [ ] ', linha: true },
  { rotulo: '❝', titulo: 'Citação',   prefixo: '> ',    linha: true },
];

export const NotaEditor: React.FC<{
  session: any;
  nota: any;
  onFechar: () => void;
  onSalvo: (n: any) => void;
}> = ({ session, nota, onFechar, onSalvo }) => {
  const [titulo, setTitulo] = useState(nota.title || '');
  const [texto, setTexto] = useState(nota.text || '');
  const [fixada, setFixada] = useState(!!nota.pinned);
  const [memorizar, setMemorizar] = useState(!!nota.memorize);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mencoes, setMencoes] = useState<any[]>([]);
  const [sugestoes, setSugestoes] = useState<any[]>([]);
  const [tipoSugestao, setTipoSugestao] = useState<'tag' | 'nota' | null>(null);
  const [tags, setTags] = useState<any[]>([]);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let vivo = true;
    api.mencoesDaNota(session, nota.id).then((r) => { if (vivo) setMencoes(r.mencoes || []); }).catch(() => {});
    api.listarTags(session).then((r) => { if (vivo) setTags(r.tags || []); }).catch(() => {});
    return () => { vivo = false; };
  }, [session, nota.id]);

  /** Aplica uma ferramenta na seleção atual. */
  const aplicar = (f: any) => {
    const el = areaRef.current;
    if (!el) return;
    const ini = el.selectionStart;
    const fim = el.selectionEnd;
    const antes = texto.slice(0, ini);
    const sel = texto.slice(ini, fim);
    const depois = texto.slice(fim);

    if (f.linha) {
      // Marcação de linha entra no COMEÇO da linha do cursor, não no meio da
      // palavra — "## " no miolo de uma frase não vira título nenhum.
      const quebra = antes.lastIndexOf('\n') + 1;
      const novo = texto.slice(0, quebra) + f.prefixo + texto.slice(quebra);
      setTexto(novo);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(ini + f.prefixo.length, fim + f.prefixo.length);
      });
      return;
    }
    const novo = antes + f.envolve + (sel || '') + f.volta + depois;
    setTexto(novo);
    requestAnimationFrame(() => {
      el.focus();
      const p = ini + f.envolve.length;
      el.setSelectionRange(p, p + sel.length);
    });
  };

  /** Autocompletar: olha o que está sendo digitado ANTES do cursor. */
  const aoDigitar = useCallback(async (valor: string) => {
    setTexto(valor);
    const el = areaRef.current;
    if (!el) return;
    const ate = valor.slice(0, el.selectionStart);

    const mTag = ate.match(/(^|\s)#([\p{L}\p{N}_-]{1,40})$/u);
    if (mTag) {
      const q = mTag[2].toLowerCase();
      setTipoSugestao('tag');
      setSugestoes(tags.filter((t) => t.slug.includes(q)).slice(0, 6));
      return;
    }
    const mLink = ate.match(/\[\[([^\]\n]{1,60})$/);
    if (mLink) {
      setTipoSugestao('nota');
      try {
        const r = await api.buscarNotas(session, mLink[1]);
        setSugestoes((r.notas || []).filter((n: any) => n.id !== nota.id));
      } catch { setSugestoes([]); }
      return;
    }
    setTipoSugestao(null);
    setSugestoes([]);
  }, [tags, session, nota.id]);

  /** Troca o pedaço em digitação pela sugestão escolhida. */
  const escolher = (s: any) => {
    const el = areaRef.current;
    if (!el) return;
    const pos = el.selectionStart;
    const ate = texto.slice(0, pos);
    const resto = texto.slice(pos);
    let novo = texto;
    if (tipoSugestao === 'tag') {
      novo = ate.replace(/#([\p{L}\p{N}_-]{1,40})$/u, '#' + s.name + ' ') + resto;
    } else {
      novo = ate.replace(/\[\[([^\]\n]{0,60})$/, '[[' + s.title + ']] ') + resto;
    }
    setTexto(novo);
    setSugestoes([]);
    setTipoSugestao(null);
    requestAnimationFrame(() => el.focus());
  };

  const salvar = async () => {
    setSalvando(true); setErro(null);
    try {
      const r = await api.editarNota(session, nota.id, {
        title: titulo.trim() || null,
        text: texto,
        pinned: fixada,
        memorize: memorizar,
      });
      onSalvo(r);
      onFechar();
    } catch (e: any) {
      setErro(e?.message || 'Não deu para salvar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000, background: 'var(--paper)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* topo */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 18px 10px', borderBottom: '1px solid var(--line)',
      }}>
        <button onClick={onFechar} aria-label="Fechar" style={{
          width: 34, height: 34, borderRadius: '50%', background: 'var(--paper-2)',
          border: '1px solid var(--line)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><Icon name="close" size={15} color="var(--ink-2)" /></button>

        <button onClick={() => setFixada((v) => !v)} title="Fixar" style={chip(fixada)}>
          <Icon name="star" size={13} color={fixada ? 'var(--ember)' : 'var(--ink-4)'} />
          Fixada
        </button>
        <button onClick={() => setMemorizar((v) => !v)} title="Para memorizar" style={chip(memorizar)}>
          <Icon name="bookmark" size={13} color={memorizar ? 'var(--ember)' : 'var(--ink-4)'} />
          Memorizar
        </button>

        <button onClick={salvar} disabled={salvando} style={{
          marginLeft: 'auto', padding: '9px 16px', borderRadius: 999, border: 'none',
          background: salvando ? 'var(--paper-3)' : 'var(--ember)',
          color: salvando ? 'var(--ink-4)' : 'var(--paper)',
          fontSize: 13.5, fontWeight: 600, cursor: salvando ? 'default' : 'pointer', fontFamily: 'inherit',
        }}>{salvando ? 'Salvando…' : 'Salvar'}</button>
      </div>

      {erro && (
        <div style={{ padding: '10px 18px 0', fontSize: 13, color: 'var(--ember-2)' }}>{erro}</div>
      )}

      {/* título */}
      <input
        value={titulo}
        onChange={(e) => setTitulo(e.target.value)}
        placeholder="Título (opcional)"
        style={{
          border: 'none', background: 'transparent', outline: 'none',
          padding: '16px 18px 6px', fontFamily: 'Instrument Serif, Georgia, serif',
          fontSize: 26, color: 'var(--ink)', width: '100%',
        }}
      />

      {/* barra de marcação */}
      <div style={{ display: 'flex', gap: 6, padding: '0 18px 8px', overflowX: 'auto' }}>
        {FERRAMENTAS.map((f) => (
          <button key={f.rotulo} onClick={() => aplicar(f)} title={f.titulo} style={{
            flexShrink: 0, minWidth: 34, height: 30, borderRadius: 9, cursor: 'pointer',
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            color: 'var(--ink-2)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          }}>{f.rotulo}</button>
        ))}
      </div>

      {/* corpo */}
      <textarea
        ref={areaRef}
        value={texto}
        onChange={(e) => aoDigitar(e.target.value)}
        placeholder={'Escreva aqui.\n\nUse # para etiquetar e [[ ]] para citar outra nota.'}
        style={{
          flex: 1, border: 'none', background: 'transparent', outline: 'none', resize: 'none',
          padding: '4px 18px 18px', fontSize: 15.5, lineHeight: 1.65,
          color: 'var(--ink)', fontFamily: 'inherit',
        }}
      />

      {/* autocompletar */}
      {sugestoes.length > 0 && (
        <div style={{
          position: 'absolute', left: 14, right: 14, bottom: 96, zIndex: 2,
          background: 'var(--paper-2)', border: '1px solid var(--line-strong)',
          borderRadius: 16, overflow: 'hidden', boxShadow: '0 18px 40px -18px rgba(0,0,0,.5)',
        }}>
          <div className="mono" style={{ ...EYEBROW, padding: '9px 14px 4px' }}>
            {tipoSugestao === 'tag' ? 'Etiquetas' : 'Notas'}
          </div>
          {sugestoes.map((s) => (
            <button key={s.id} onClick={() => escolher(s)} style={{
              width: '100%', textAlign: 'left', padding: '11px 14px', cursor: 'pointer',
              background: 'transparent', border: 'none', borderTop: '1px solid var(--line)',
              color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
            }}>
              {tipoSugestao === 'tag' ? '#' + s.name : s.title}
            </button>
          ))}
        </div>
      )}

      {/* Mencionada em — a leitura inversa do [[ ]] */}
      {mencoes.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line)', padding: '12px 18px 16px' }}>
          <div className="mono" style={EYEBROW}>Mencionada em</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 8 }}>
            {mencoes.map((m) => (
              <span key={m.id} style={{
                padding: '6px 11px', borderRadius: 999, fontSize: 12.5,
                background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink-2)',
              }}>{m.title}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function chip(ligado: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '7px 11px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
    fontSize: 12, fontWeight: ligado ? 700 : 500,
    background: ligado ? 'var(--ember-soft)' : 'var(--paper-2)',
    border: `1px solid ${ligado ? 'var(--ember)' : 'var(--line)'}`,
    color: ligado ? 'var(--ember-2)' : 'var(--ink-3)',
  };
}

export default NotaEditor;
